import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, LifeBuoy, Moon, Sparkles, Sun } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useSiteBrand } from '../../context/SiteBrandContext';
import '../../pages/Private/adminTheme.css';

/**
 * The dashboard's own top bar (/private). It replaces the public site's menu
 * there: an admin needs the brand, help for the page they are on, a way back
 * to the live site and the light/dark switch — not the shop navigation.
 *
 * Rendered as `nav.fixed` on purpose: PrivatePage measures that element's
 * bottom edge to place the sidebar and content under it.
 */
const AdminTopBar = () => {
    const { isDark, toggleTheme } = useTheme();
    const { brand } = useSiteBrand();
    // Wide lockup first when the site uses one (its square logo may not
    // exist); each failing image falls through to the next, then initials.
    const wide = isDark ? (brand.logoWideDarkUrl || brand.logoWideUrl) : (brand.logoWideUrl || brand.logoWideDarkUrl);
    const candidates = [brand.logoMode === 'wide' ? wide : '', brand.logoUrl, wide].filter(Boolean)
        .filter((u, i, all) => all.indexOf(u) === i);
    const [failed, setFailed] = useState(0);
    const raw = candidates[failed] || '';
    const logo = raw.includes('res.cloudinary.com/') ? raw.replace('/upload/', '/upload/h_80,c_fit,q_auto,f_auto/') : raw;
    const setLogoError = () => setFailed((n) => n + 1);

    // PrivatePage owns the per-page help drawer and listens for this; before
    // it is on screen (sign-in), Help opens the Help Center instead.
    const navigate = useNavigate();
    const openHelp = () => {
        if (window.__hmAdminHelpReady) window.dispatchEvent(new CustomEvent('hm-admin-open-help'));
        else navigate('/private/help');
    };

    return (
        <nav className="fixed top-0 inset-x-0 z-[60] admin-topbar" aria-label="Dashboard">
            <div className="h-14 flex items-center gap-3 ps-14 md:ps-5 pe-3 sm:pe-5">
                <Link to="/private" className="flex items-center gap-2.5 min-w-0" aria-label={`${brand.siteName || 'Site'} dashboard`}>
                    {logo
                        ? <span className={`admin-topbar-logo${isDark && raw === brand.logoWideDarkUrl ? " is-plain" : ""}`}><img src={logo} alt="" onError={setLogoError} /></span>
                        : <span className="admin-topbar-mark">{(brand.siteName || 'S').trim().charAt(0).toUpperCase()}</span>}
                    <span className="truncate text-sm font-semibold admin-topbar-name">{brand.siteName || 'Dashboard'}</span>
                    <span className="hidden sm:inline text-xs font-medium admin-topbar-tag">Dashboard</span>
                </Link>
                <div className="ms-auto flex items-center gap-1.5">
                    {/* Opens the dashboard assistant (PrivatePage listens; Ctrl/Cmd+K too). */}
                    <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('hm-admin-assistant'))}
                        className="admin-topbar-btn admin-topbar-ai" title="Assistant (Ctrl+K)">
                        <Sparkles size={16} aria-hidden="true" /> <span className="hidden sm:inline">Assistant</span>
                        <kbd className="hidden lg:inline">Ctrl K</kbd>
                    </button>
                    <button type="button" onClick={openHelp} className="admin-topbar-btn">
                        <LifeBuoy size={16} aria-hidden="true" /> <span className="hidden sm:inline">Help</span>
                    </button>
                    <a href="/" target="_blank" rel="noopener noreferrer" className="admin-topbar-btn">
                        <ExternalLink size={16} aria-hidden="true" /> <span className="hidden sm:inline">View site</span>
                    </a>
                    <button type="button" onClick={toggleTheme} className="admin-topbar-icon"
                        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} title={isDark ? 'Light mode' : 'Dark mode'}>
                        {isDark ? <Sun size={17} /> : <Moon size={17} />}
                    </button>
                </div>
            </div>
        </nav>
    );
};

export default AdminTopBar;
